import { createClient } from '@supabase/supabase-js'
import { validateAnonKey } from '@/utils/supabaseKeyValidator'
import { getCachedUser } from '@/utils/authCache'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  const missing = []
  if (!supabaseUrl) missing.push('VITE_SUPABASE_URL')
  if (!supabaseAnonKey) missing.push('VITE_SUPABASE_ANON_KEY')
  const isProd = Boolean(import.meta?.env?.PROD)
  const hint = isProd
    ? 'This is a production build. Ensure your Cloud Build/Docker build args are set and mapped from Secret Manager.'
    : 'Add these to your .env.local file and restart the dev server.'
  throw new Error(`Supabase config missing: ${missing.join(', ')}. ${hint}`)
}

// SECURITY: Validate that the anon key is not a service role key
// Service role keys must NEVER be exposed in client-side code
try {
  validateAnonKey(supabaseAnonKey, 'VITE_SUPABASE_ANON_KEY')
} catch (error) {
  console.error('❌ CRITICAL SECURITY ERROR:', error.message)
  throw error
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'x-application-name': 'mysummersquad-web',
    },
  }
  // Removed global headers - CSRF not needed for Supabase (uses JWT tokens)
})

// Helper function to handle Supabase errors
export const handleSupabaseError = (error) => {
  console.error('Supabase error:', error)
  
  // Preserve more error details for better debugging
  const errorMessage = error.message || 'Database operation failed'
  const errorDetails = error.details || error.hint || ''
  const errorCode = error.code || ''
  
  const fullMessage = errorCode 
    ? `${errorMessage} (${errorCode})${errorDetails ? ': ' + errorDetails : ''}`
    : errorMessage
    
  throw new Error(fullMessage)
}

// Helper function to get current user (with caching to reduce auth calls)
export const getCurrentUser = async () => {
  const user = await getCachedUser(supabase)
  
  // Ensure user has a valid ID
  if (!user.id) {
    console.error('User object missing ID:', user)
    throw new Error('Invalid user data: missing ID')
  }
  
  if (import.meta.env?.DEV) console.log('Current user:', user.id)
  return user
}

// Helper function to get current session
export const getCurrentSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession()
  if (error) throw error
  return session
}
